import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useProfileStore } from '@/stores/useProfileStore';

export interface UserGym {
  id: string;
  name: string;
  is_primary: boolean;
}

interface UseUserGymsResult {
  gyms: UserGym[];
  activeGym: UserGym | null;
  setActiveGym: (gym: UserGym) => void;
  loading: boolean;
}

/**
 * Fetches all active gym-type organization memberships for the current user.
 * Returns a list of gyms plus an activeGym (defaults to primary / home_gym_id).
 */
export function useUserGyms(): UseUserGymsResult {
  const profile = useProfileStore((s) => s.profile);
  const [gyms, setGyms] = useState<UserGym[]>([]);
  const [activeGym, setActiveGym] = useState<UserGym | null>(null);
  const [loading, setLoading] = useState(true);

  const userId = profile?.id ?? null;
  const homeGymId = profile?.home_gym_id ?? profile?.gym_id ?? null;

  const load = useCallback(async () => {
    if (!userId) {
      setGyms([]);
      setActiveGym(null);
      setLoading(false);
      return;
    }

    try {
      // Query organization_memberships joined with organizations (gym type)
      const { data, error } = await supabase
        .from('organization_memberships')
        .select('org_id, is_primary_for_type, organizations(id, name)')
        .eq('user_id', userId)
        .eq('status', 'active');

      if (error) throw error;

      // Filter to gym-type orgs and map
      const gymRows: UserGym[] = (data ?? [])
        .filter((row: any) => row.organizations)
        .map((row: any) => ({
          id: row.organizations.id ?? row.org_id,
          name: row.organizations.name ?? 'Unknown Gym',
          is_primary: row.is_primary_for_type || row.org_id === homeGymId,
        }));

      // If no org memberships but user has a home_gym_name, create a synthetic entry
      if (gymRows.length === 0 && homeGymId && profile?.home_gym_name) {
        gymRows.push({
          id: homeGymId,
          name: profile.home_gym_name,
          is_primary: true,
        });
      }

      setGyms(gymRows);

      // Default to primary gym or first
      const primary = gymRows.find((g) => g.is_primary) ?? gymRows[0] ?? null;
      setActiveGym((prev) => {
        // Keep current selection if still valid
        if (prev && gymRows.some((g) => g.id === prev.id)) return prev;
        return primary;
      });
    } catch {
      // Fallback: use profile home gym if available
      if (homeGymId && profile?.home_gym_name) {
        const fallback: UserGym = { id: homeGymId, name: profile.home_gym_name, is_primary: true };
        setGyms([fallback]);
        setActiveGym(fallback);
      }
    } finally {
      setLoading(false);
    }
  }, [userId, homeGymId, profile?.home_gym_name]);

  useEffect(() => {
    load();
  }, [load]);

  return { gyms, activeGym, setActiveGym, loading };
}
